/** R2 storage configuration card — admin form for custom R2 / S3-compatible credentials */

import { useState } from 'react'
import { Settings2, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useT, type TranslationKey } from '../../i18n'
import {
  useStorageConfig,
  useUpdateStorageConfig,
  useDeleteStorageConfig,
  useTestStorageConnection,
  type StorageConfig,
} from '../../hooks/use-storage-settings'
import { buttonDangerClass, buttonPrimaryClass, buttonSecondaryClass, cardClass, errorTextClass, inputClass, labelClass } from '../ui/styles'

type FormField = 'accountId' | 'bucketName' | 'endpointUrl' | 'accessKey' | 'secretKey'

export function StorageConfigCard() {
  const t = useT()
  const { data } = useStorageConfig()
  const updateConfig = useUpdateStorageConfig()
  const deleteConfig = useDeleteStorageConfig()
  const testConnection = useTestStorageConnection()

  const config: StorageConfig | null | undefined = data?.config

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<Record<FormField, string>>({ accountId: '', accessKey: '', secretKey: '', bucketName: '', endpointUrl: '' })
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null)

  const handleToggle = () => {
    if (!showForm && config) {
      setForm({ accountId: config.accountId, bucketName: config.bucketName, accessKey: '', secretKey: '', endpointUrl: config.endpointUrl ?? '' })
    }
    setShowForm((v) => !v)
    setTestResult(null)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    await updateConfig.mutateAsync({
      accountId: form.accountId,
      bucketName: form.bucketName,
      accessKey: form.accessKey || '__unchanged__',
      secretKey: form.secretKey || '__unchanged__',
      endpointUrl: form.endpointUrl || null,
    })
    setShowForm(false)
  }

  const handleTest = async () => {
    setTestResult(await testConnection.mutateAsync(undefined))
  }

  const handleDelete = async () => {
    if (!window.confirm(t('settings.storage.config.removeConfirm'))) return
    await deleteConfig.mutateAsync()
    setShowForm(false)
  }

  const field = (name: FormField, labelKey: TranslationKey, opts: { secret?: boolean; placeholder?: string; required?: boolean; wide?: boolean } = {}) => (
    <div className={cn('space-y-1.5', opts.wide && 'sm:col-span-2')}>
      <label htmlFor={`storage-${name}`} className={labelClass}>
        {t(labelKey)}
      </label>
      <input
        id={`storage-${name}`}
        type={opts.secret ? 'password' : 'text'}
        autoComplete="off"
        className={inputClass}
        value={form[name]}
        onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
        placeholder={opts.placeholder}
        required={opts.required}
      />
    </div>
  )

  return (
    <div className={cn(cardClass, 'space-y-3 p-4')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-accent-text" aria-hidden="true" />
          <span className="text-sm font-semibold text-fg">{t('settings.storage.config.title')}</span>
          {config ? (
            <span className={cn('rounded-md px-1.5 py-0.5 text-xs font-medium', config.isVerified ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning')}>
              {config.isVerified ? t('settings.storage.config.verified') : t('settings.storage.config.notVerified')}
            </span>
          ) : (
            <span className="rounded-md bg-surface-muted px-1.5 py-0.5 text-xs font-medium text-fg-subtle">{t('settings.storage.config.default')}</span>
          )}
        </div>
        <button type="button" onClick={handleToggle} aria-expanded={showForm} className={buttonSecondaryClass}>
          {showForm ? t('settings.storage.config.hide') : t('settings.ai.configure')}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {field('accountId', 'settings.storage.config.accountId', { required: true })}
            {field('bucketName', 'settings.storage.config.bucketName', { placeholder: 'my-bucket', required: true })}
            {field('endpointUrl', 'settings.storage.config.endpointUrl', { placeholder: 'https://s3.us-east-1.amazonaws.com', wide: true })}
            {field('accessKey', 'settings.storage.config.accessKey', {
              secret: true,
              placeholder: config?.hasAccessKey ? t('settings.ai.keySaved') : undefined,
            })}
            {field('secretKey', 'settings.storage.config.secretKey', {
              secret: true,
              placeholder: config?.hasSecretKey ? t('settings.ai.keySaved') : undefined,
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" disabled={updateConfig.isPending} className={buttonPrimaryClass}>
              {updateConfig.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('settings.save')}
            </button>
            <button type="button" onClick={() => void handleTest()} disabled={testConnection.isPending || !config} className={buttonSecondaryClass}>
              {testConnection.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('settings.storage.config.test')}
            </button>
            {config && (
              <button type="button" onClick={() => void handleDelete()} disabled={deleteConfig.isPending} className={buttonDangerClass}>
                {t('settings.remove')}
              </button>
            )}
          </div>

          {testResult && (
            <p role="status" className={cn('text-sm', testResult.success ? 'text-success' : 'text-danger')}>
              {testResult.success ? t('settings.storage.config.testOk') : (testResult.error ?? t('settings.storage.config.testFailed'))}
            </p>
          )}
          {updateConfig.isError && (
            <p role="alert" className={errorTextClass}>
              {t('settings.saveFailed', { message: updateConfig.error instanceof Error ? updateConfig.error.message : String(updateConfig.error) })}
            </p>
          )}
        </form>
      )}
    </div>
  )
}
